// DeviceSearchPalette — search bar that drops down under the Navbar
// Opens via a Navbar icon or Cmd+K / Ctrl+K

import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import DeviceStatusIcon from "./DeviceStatusIcon";

const API_URL = config.API_URL;

export default function DeviceSearchPalette({ isOpen, onClose }) {
  const navigate = useNavigate();
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [devices, setDevices] = useState([]);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && ORG_ID) {
      axios.get(`${API_URL}/organizations/${ORG_ID}/devices`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      })
        .then(res => setDevices(res.data))
        .catch(() => setDevices([]));
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) setQuery("");
  }, [isOpen, ORG_ID]);

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === "Escape") onClose();
    }
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const results = useMemo(() => {
    if (!query.trim()) return devices.slice(0, 8);
    const q = query.toLowerCase();
    return devices
      .filter(d => d.hostname?.toLowerCase().includes(q) || d.serial_number?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, devices]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 top-[73px] bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border-b border-gray-800 shadow-2xl w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
            <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by hostname or serial number..."
              className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder-gray-500"
            />
            <kbd className="text-gray-600 text-xs border border-gray-700 rounded px-1.5 py-0.5">Esc</kbd>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {results.length === 0 ? (
              <p className="text-gray-500 text-sm px-4 py-6 text-center">
                {query.trim() ? "No devices found." : "Start typing to search your devices."}
              </p>
            ) : (
              results.map(d => (
                <div
                  key={d.id}
                  onClick={() => { navigate(`/devices/${d.id}`); onClose(); }}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-gray-800 cursor-pointer border-b border-gray-800 last:border-0 transition-colors"
                >
                  <DeviceStatusIcon online={d.is_online} className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{d.hostname}</p>
                    <p className="text-gray-500 text-xs truncate">{d.manufacturer} {d.model} · {d.serial_number || "No serial"}</p>
                    <p className="text-gray-600 text-xs">Last seen {new Date(d.last_seen).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}